"use client"

import { useEffect, useState } from "react"

// Project-wide loading indicator.
//
// Replaces the per-page "border-2 ... animate-spin" wheels that ended up
// at varied positions (often top-left where the back button lives) with
// a single centered, branded loader.
//
// Visual: the same 3px green bar as the login splash, filling. A page load
// has no real progress signal (the data arrives when it arrives), so the
// fill is a curve, not a measurement: it moves fast through the first half,
// slows as it climbs, and settles just short of full until the page takes
// over. It never reaches the end on its own — full is the page being there.
// The number ticker this replaced counted the same curve; the bar reads it
// at a glance without asking anyone to read a figure, and it matches the
// loader they saw on the way in.

interface PageLoaderProps {
  /** Optional caption rendered below the bar. */
  message?: string
  /**
   * When true (default), takes the full viewport via fixed positioning
   * so the loader is centered regardless of any sticky/fixed page
   * chrome above it. Set false to fill the nearest positioned ancestor
   * (use inside a wrapper with position:relative).
   */
  fullScreen?: boolean
}

// Time constant of the climb. At 900ms the bar is past 60% in half a
// second and past 90% by two — quick enough that a normal load shows real
// movement, slow enough that a slow one doesn't sit near full for ages.
const TAU_MS = 900
const CEILING = 0.99

/** 0 → 0.99 along an ease-out curve, driven by requestAnimationFrame. */
export function useLoadProgress(ceiling = CEILING) {
  const [progress, setProgress] = useState(0)
  useEffect(() => {
    if (typeof window === "undefined") return
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    let raf = 0
    const start = performance.now()
    const tick = (now: number) => {
      const t = now - start
      const next = Math.min(ceiling, ceiling * (1 - Math.exp(-t / TAU_MS)))
      setProgress(next)
      if (next < ceiling) raf = requestAnimationFrame(tick)
    }
    if (reduced) {
      setProgress(ceiling)
      return
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [ceiling])
  return progress
}

export function PageLoader({ message, fullScreen = true }: PageLoaderProps) {
  const positionCls = fullScreen ? "fixed inset-0 z-30" : "absolute inset-0"
  const progress = useLoadProgress()

  return (
    <div className={`${positionCls} flex items-center justify-center bg-background pointer-events-none`}>
      <div
        className="flex flex-col items-center gap-3"
        role="status"
        aria-live="polite"
        aria-label={message || "Loading"}
      >
        {/* Same bar as the login splash: 3px track, brand green fill driven
            by transform so it stays smooth while the page mounts behind it.
            The track takes the foreground colour at low alpha so it reads on
            either theme, where the splash can assume its black screen. Green
            is inline with a hex fallback for the same reason as the splash:
            a missing utility would leave an invisible fill. */}
        <div className="h-[3px] w-40 overflow-hidden rounded-full bg-foreground/15">
          <div
            className="h-full w-full rounded-full"
            style={{
              backgroundColor: "var(--color-booth-green, #10b981)",
              transformOrigin: "left",
              transform: `scaleX(${progress})`,
            }}
          />
        </div>
        {message && (
          <p className="text-sm" style={{ color: "#696969" }}>
            {message}
          </p>
        )}
      </div>
    </div>
  )
}

// Inline section loader. Three pulsing dots with no positioning chrome —
// drop into any flex/block context where a section is waiting on data
// (feed loading, list loading, etc.).
// Use this instead of <Loader2 className="animate-spin" /> for
// consistency with the rest of the app's loading visuals.
export function InlineLoader({ className = "" }: { className?: string }) {
  return (
    <div className={`flex items-center justify-center ${className}`} role="status" aria-label="Loading">
      <div className="flex items-center gap-1.5">
        <span className="booth-loader-dot-inline" style={{ animationDelay: "-0.32s" }} />
        <span className="booth-loader-dot-inline" style={{ animationDelay: "-0.16s" }} />
        <span className="booth-loader-dot-inline" style={{ animationDelay: "0s" }} />
      </div>
      <style jsx>{`
        .booth-loader-dot-inline {
          width: 7px;
          height: 7px;
          border-radius: 9999px;
          background-color: #696969;
          display: inline-block;
          animation: boothLoaderPulseInline 1.4s ease-in-out infinite;
        }
        @keyframes boothLoaderPulseInline {
          0%, 80%, 100% { opacity: 0.25; transform: scale(0.85); }
          40% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  )
}
